import React, { useState, useCallback, useRef, useEffect } from "react";
import Modal from "react-modal";
import CustomInput from "../../common/CustomInput";
import PhoneInput from "../../common/PhoneInput";
import RaffleApi from "../../../services/resources/RaffleApi";
import { ApiCallErrorMessageHandler } from "../../../services/ApiClientErrorHandler";
import { trimTrailingSlash, validateEmail } from "../../../lib/utils";
import axios from "axios";

const createSlug = (value) => value.trim().replaceAll(" ", "-").toLowerCase();

export default function CreateTeamModal(props) {
  const { countries, onClose, onCreateTeam, sellerEnrollmentId, teams } = props;

  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [validationErrors, setValidationErrors] = useState({});
  const [submitAttempted, setSubmitAttempted] = useState(false);

  const [team, setTeam] = useState({
    email: "",
    goal: "",
    phone: "",
    phoneCountryID: "",
    name: "",
    sellerEnrollmentId,
    slug: "",
  });

  const apiSignalRef = useRef(axios.CancelToken.source());
  useEffect(() => {
    const _apiSignal = apiSignalRef.current;
    return () => {
      _apiSignal.cancel();
    };
  }, []);

  const isTeamValid = useCallback(() => {
    return (
      !!team.name &&
      !!team.slug &&
      !!team.email &&
      !validationErrors.name &&
      !validationErrors.slug &&
      !validationErrors.email &&
      !validationErrors.phone &&
      !validationErrors.goal
    );
  }, [team, validationErrors]);

  const onSubmit = useCallback(async () => {
    setSubmitAttempted(true);

    if (!isTeamValid()) return;

    setLoading(true);
    setErrorMessage("");

    try {
      const savedTeam = await RaffleApi.createTeam(
        apiSignalRef.current.token,
        team
      );
      savedTeam && onCreateTeam(savedTeam);
      onClose(team.slug);
    } catch (err) {
      if (!axios.isCancel(err)) {
        setErrorMessage(ApiCallErrorMessageHandler(err));
        setLoading(false);
      }
    }
  }, [isTeamValid, onClose, onCreateTeam, team]);

  const onChangeTeam = useCallback((name, value, otherChanges = {}) => {
    setTeam((team) => ({ ...team, [name]: value, ...otherChanges }));
  }, []);

  const onChangeValidationErrors = useCallback((name, isValid) => {
    setValidationErrors((errors) => ({ ...errors, [name]: isValid }));
  }, []);

  const isTeamDataTaken = useCallback(
    (name, value) => {
      return teams.some(
        (t) => t[name].toLowerCase().trim() === value.toLowerCase().trim()
      );
    },
    [teams]
  );

  return (
    <Modal
      isOpen={true}
      className="team-modal"
      bodyOpenClassName="modal-background-no-scroll"
    >
      <p className="team-modal-title">Create your team</p>
      <div className="team-modal-inputs-grid">
        <div
          className={`team-modal-input-container ${
            (submitAttempted && !team.name) || validationErrors.name
              ? "error"
              : ""
          }`}
        >
          <label>Team Name</label>
          <div>
            <CustomInput
              error={(submitAttempted && !team.name) || validationErrors.name}
              name="name"
              onBlur={(e) => {
                const isInvalidName = isTeamDataTaken("name", e.target.value);
                onChangeValidationErrors("name", isInvalidName);

                // set slug when name changes
                const slug = createSlug(e.target.value);
                if (!isTeamDataTaken("slug", slug)) {
                  onChangeTeam("slug", slug);
                }
              }}
              onChange={(e) => onChangeTeam(e.target.name, e.target.value)}
              type="text"
              value={team.name}
            />
            {!!validationErrors.name && (
              <span className="error-text">Team name already taken</span>
            )}
          </div>
        </div>

        <div>
          <div
            className={`team-modal-input-container ${
              (submitAttempted && !team.slug) || validationErrors.slug
                ? "error"
                : ""
            }`}
          >
            <label>Team URL</label>
            <div>
              <CustomInput
                error={(submitAttempted && !team.slug) || validationErrors.slug}
                name="slug"
                onBlur={(e) => {
                  // slugify the value on blur
                  const slug = createSlug(e.target.value);
                  onChangeTeam("slug", slug);

                  const isInvalidSlug = isTeamDataTaken("slug", slug);
                  onChangeValidationErrors("slug", isInvalidSlug);
                }}
                onChange={(e) => onChangeTeam(e.target.name, e.target.value)}
                type="text"
                value={team.slug}
              />
              {validationErrors.slug ? (
                <span className="error-text">Team URL already taken</span>
              ) : (
                <span className="sub-text">
                  {window.location.origin}
                  {trimTrailingSlash(window.location.pathname)}/{team.slug}
                </span>
              )}
            </div>
          </div>
        </div>

        <div
          className={`team-modal-input-container ${
            (submitAttempted && !team.email) || validationErrors.email
              ? "error"
              : ""
          }`}
        >
          <label>Notification Email</label>
          <div>
            <CustomInput
              autoComplete="email"
              error={(submitAttempted && !team.email) || validationErrors.email}
              name="email"
              onBlur={(e) => {
                const isValidEmail =
                  !e.target.value || validateEmail(e.target.value);
                onChangeValidationErrors("email", !isValidEmail);
              }}
              onChange={(e) => onChangeTeam(e.target.name, e.target.value)}
              type="text"
              value={team.email}
            />
            {validationErrors.email ? (
              <span className="error-text">Invalid email address</span>
            ) : (
              <span className="sub-text">
                Notifications will be sent to this email address for all
                donations.
              </span>
            )}
          </div>
        </div>

        <div
          className={`team-modal-input-container ${
            validationErrors.phone ? "error" : ""
          }`}
        >
          <label className="label-with-sub">Phone</label>
          <PhoneInput
            countries={countries}
            error={validationErrors.phone}
            name="phone"
            onChange={(phone, phoneCountryId) =>
              onChangeTeam("phone", phone, phoneCountryId)
            }
            validate={(isValid) => onChangeValidationErrors("phone", !isValid)}
            value={team.phone}
          />
        </div>

        <div
          className={`team-modal-input-container ${
            validationErrors.goal ? "error" : ""
          }`}
        >
          <label>Goal</label>
          <div className="flex flex-align-center">
            <CustomInput
              error={validationErrors.goal}
              min={1}
              name="goal"
              onBlur={(e) => {
                const isValidGoal =
                  !e.target.value || Number(e.target.value) >= 0;
                onChangeValidationErrors("goal", !isValidGoal);
              }}
              onChange={(e) => onChangeTeam(e.target.name, e.target.value)}
              type="number"
              value={team.goal}
            />
            <label className="goal-tickets-text">Tickets</label>
          </div>
          {!!validationErrors.goal && (
            <span className="error-text">Invalid Goal</span>
          )}
        </div>
      </div>

      <div className="team-modal-footer">
        <button className="btn-cancel" onClick={() => onClose()}>
          Cancel
        </button>
        <button className="btn-save" disabled={loading} onClick={onSubmit}>
          {loading ? "Adding Team..." : "Add Team"}
        </button>
      </div>
      {submitAttempted && !!errorMessage && (
        <p className="team-modal-error-message">{errorMessage}</p>
      )}
    </Modal>
  );
}
